"use client";

import React from "react";

import {cn} from "@/lib/utils";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import Image from "next/image";
import UnicornWrapper from "@/components/global/utils/UnicornWrapper";

import {PageKomplettausbildungSchema} from "@/sanity/pages/PageKomplettausbildungTemplate";

import unicornPlaceholder from "@/public/global/footer-placeholder.png";
import slide1 from "@/public/komplettausbildung/slide1.jpg";
import slide3 from "@/public/komplettausbildung/slide3.jpg";
import slide4 from "@/public/komplettausbildung/slide4.jpg";


export default function HeroSection({
  data,
  className
}: {
  data: PageKomplettausbildungSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      <div className="relative pt-[6rem] lg:pt-[11rem] pb-[3rem] lg:pb-[12rem]">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />
        <DecorationLine
          className="bottom-0 left-0"
          orientation="horizontal"
        />

        <div className="container relative flex flex-col items-center">
          <div className="w-[min(187.75rem,100%)] flex flex-col items-center lg:items-start">
            <Chip
              className="prose-img:!w-[3rem]"
              image={data?.heroChip?.icon}
              text={data?.heroChip?.text}
            />

            {data?.heroTitle && (
              <div className="richtext lg:richtext-start prose-p:w-[min(85.5rem,100%)] prose-headings:w-[min(110.5rem,100%)]">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.heroCTA && (
              <CTACornerButton
                className="mt-[4rem]"
                data={data.heroCTA}
              />
            )}
          </div>
        </div>
      </div>

      {data?.heroItems && (
        <div className="relative py-[2rem]">
          <DecorationLine
            className="bottom-0 left-0"
            orientation="horizontal"
          />

          <div className="flex overflow-clip">
            <div
              className="flex-shrink-0 flex gap-[1rem] will-change-transform"
              style={{
                animation: `scrollLeft ${data.heroItems.length * 4}s linear infinite`,
              }}
            >
              {[...data.heroItems, ...data.heroItems].map((item, index) => {
                const slideType = index % 4;

                if (slideType === 0) {
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 aspect-[715/434] h-[34.125rem] lg:h-[54.25rem] rounded-[1rem] bg-[#0A1A1D] relative overflow-clip px-[2.5rem] py-[3rem] lg:px-[4rem] lg:py-[5rem] flex flex-col items-start justify-end"
                    >
                      <Image
                        className="absolute top-0 left-0 !w-full !h-full object-cover"
                        src={slide1}
                        alt="man holding tablet"
                      />

                      {item?.text && (
                        <div className="richtext richtext-start prose-p:text-[4.5rem] lg:prose-p:text-[7rem] prose-p:font-ivy prose-p:italic prose-p:leading-none prose-p:text-white/70 prose-strong:text-white prose-strong:font-light prose-p:font-light relative">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                if (slideType === 1) {
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 aspect-square h-[34.125rem] lg:h-[54.25rem] rounded-[1rem] bg-[#0A1A1D] relative overflow-clip px-[2.5rem] py-[3rem] lg:px-[4rem] lg:py-[5rem]"
                    >
                      {item?.text && (
                        <div className="richtext richtext-start prose-p:text-[4.5rem] lg:prose-p:text-[7rem] prose-p:font-ivy prose-p:italic prose-p:leading-none prose-p:text-white/70 prose-strong:text-white prose-strong:font-light prose-p:font-light relative z-10">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}

                      <div className="absolute inset-0 aspect-square opacity-20 mix-blend-screen">
                        <UnicornWrapper
                          jsonFilePath="unicorn-studio/lastCTA-logo.json"
                          placeholderImage={unicornPlaceholder}
                        />
                      </div>
                    </div>
                  );
                }

                if (slideType === 2) {
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 aspect-[715/434] h-[34.125rem] lg:h-[54.25rem] rounded-[1rem] bg-[#0A1A1D] relative overflow-clip px-[2.5rem] py-[3rem] lg:px-[4rem] lg:py-[5rem] flex flex-col items-start justify-end"
                    >
                      <Image
                        className="absolute top-0 left-0 !w-full !h-full object-cover"
                        src={slide3}
                        alt="event"
                      />

                      {item?.text && (
                        <div className="richtext richtext-start prose-p:text-[4.5rem] lg:prose-p:text-[7rem] prose-p:font-ivy prose-p:italic prose-p:leading-none prose-p:text-white/70 prose-strong:text-white prose-strong:font-light prose-p:font-light relative">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                if (slideType === 3) {
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 aspect-[715/434] h-[34.125rem] lg:h-[54.25rem] rounded-[1rem] bg-[#0A1A1D] relative overflow-clip px-[2.5rem] py-[3rem] lg:px-[4rem] lg:py-[5rem] flex flex-col items-start justify-end"
                    >
                      <Image
                        className="absolute top-0 left-0 !w-full !h-full object-cover"
                        src={slide4}
                        alt=""
                      />

                      {item?.text && (
                        <div className="richtext richtext-start prose-p:text-[4.5rem] lg:prose-p:text-[7rem] prose-p:font-ivy prose-p:italic prose-p:leading-none prose-p:text-white/70 prose-strong:text-white prose-strong:font-light prose-p:font-light relative">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
